export type OverallRiskCategory = 'SAFE' /* this is going away */ | 'OK' | 'RISKY' | 'DANGEROUS';
export type RiskCategory = 'EXTREME' | 'HIGH' | 'MEDIUM' | 'LOW';

// Normalize overall risk levels to match individual risk levels
export const normalizeRiskCategory = (riskLevel: RiskCategory | string): RiskCategory => {
  if (riskLevel === 'SAFE') return 'LOW';
  if (riskLevel === 'OK') return 'LOW';
  if (riskLevel === 'RISKY') return 'MEDIUM';
  if (riskLevel === 'DANGEROUS') return 'HIGH';
  return riskLevel as RiskCategory; // default fallback
};

export const getRiskColor = (riskLevel: RiskCategory | string): string => {
  const normalized = normalizeRiskCategory(riskLevel);
  switch (normalized) {
    case 'EXTREME': return 'crypto-red';
    case 'HIGH': return 'crypto-red';
    case 'MEDIUM': return 'crypto-yellow';
    case 'LOW': return 'crypto-accent';
    default: return 'crypto-accent';
  }
};

export const getRiskBgColor = (riskLevel: RiskCategory | string): string => {
  const normalized = normalizeRiskCategory(riskLevel);
  switch (normalized) {
    case 'EXTREME': return 'crypto-red/10';
    case 'HIGH': return 'crypto-red/10';
    case 'MEDIUM': return 'crypto-yellow/10';
    case 'LOW': return 'gray-700/30';
    default: return 'gray-700/30';
  }
};

export const getRiskBorderColor = (riskLevel: RiskCategory | string): string => {
  const normalized = normalizeRiskCategory(riskLevel);
  switch (normalized) {
    case 'EXTREME': return 'crypto-red/30';
    case 'HIGH': return 'crypto-red/30';
    case 'MEDIUM': return 'crypto-yellow/30';
    case 'LOW': return 'gray-600/50';
    default: return 'gray-600/50';
  }
};

export const getPercentageColor = (percentage: number): string => {
  if (percentage > 20) return 'text-crypto-red';
  if (percentage > 10) return 'text-crypto-yellow';
  if (percentage > 5) return 'text-crypto-accent';
  return 'text-white';
};

export interface TokenDetails {
  mint: string;
  name: string;
  symbol: string;
  uri: string;
  creator: string;
  creatorNetwork: string;
  createdAt: string;
  description: string;
  telegram: string;
  twitter: string;
  website: string;
  decimals?: number;
  networkPercentage: string;
  riskLevel: string;
  networkBundles: Array<{
    fundingSource: string;
    accounts: Array<{
      walletAddress: string;
      tokenAmount: string;
      percentage: number;
      topFundingSource: string;
      tokenAccountAddress: string;
    }>;
    uniqueAccountCount: number;
    totalPercentage: number;
  }>;
  individualHolders: Array<{
    walletAddress: string;
    tokenAmount: string;
    percentage: number;
    topFundingSource: string;
    tokenAccountAddress: string;
    riskLevel: string;
  }>;
  uniqueWalletCount: number;
  totalAccounts: number;
  topNetworkOwnership: number;
}

export const generateSocialMediaMessage = (tokenDetails: TokenDetails & { overallSafetyLevel?: string; criticalRisks?: string[]; warnings?: string[]; tokenName?: string; tokenSymbol?: string }): string => {
  // Support both field naming conventions (holder analysis uses name/symbol, token details uses tokenName/tokenSymbol)
  const name = tokenDetails.name || tokenDetails.tokenName || 'Unknown';
  const symbol = tokenDetails.symbol || tokenDetails.tokenSymbol || '???';

  // Use safety analysis warnings/risks if available (preferred)
  const safetyLevel = tokenDetails.overallSafetyLevel;
  const criticalRisks = tokenDetails.criticalRisks || [];
  const warnings = tokenDetails.warnings || [];

  if (safetyLevel) {
    if (safetyLevel === 'OK' || safetyLevel === 'SAFE') {
      return `✅ ${name} ($${symbol}) - Clean token on @deepnets_ai`;
    }

    // Pick the most important risk to feature in the tweet
    const topRisk = criticalRisks[0] || warnings[0];
    if (topRisk) {
      const label = safetyLevel === 'DANGEROUS' ? '🚨 DANGEROUS' : '⚠️ RISKY';
      return `${label}: ${name} ($${symbol}) - ${topRisk}`;
    }

    return `${name} ($${symbol}) - ${safetyLevel} token analysis on @deepnets_ai`;
  }

  // Fallback: use holder risk level if no safety analysis available
  const riskLevel = tokenDetails.riskLevel;
  if (riskLevel === 'LOW' || riskLevel === 'MEDIUM') {
    return `${name} ($${symbol}) - ${riskLevel} RISK token analysis`;
  }

  return `${name} ($${symbol}) - ${riskLevel} RISK token analysis`;
};
